import { Link } from 'react-router'
import { AUTHOR_BYLINE } from '#app/seo/author.ts'
import { formatPublicDate, formatPublicNumber } from '#app/utils/misc.tsx'

/**
 * The line under an H1 that says how current a page is and who stands behind
 * it.
 *
 * No public page carried a date before this. Recency is the strongest signal
 * a model uses when deciding whether a ranked list is worth citing, and a
 * ranking with no date is the cheapest thing for it to skip.
 *
 * ## Two dates, both labelled
 *
 * "Updated {today}" is the obvious version and the wrong one. It would change
 * on every pipeline run whether or not a single score moved, which is the
 * artificial-freshness pattern that gets sites discounted rather than cited.
 *
 * So the page states what actually happened, separately:
 *
 *   Reviews read through {newest review in the corpus}
 *   Scored {when the ranking was last recomputed}
 *
 * Neither can be refreshed by re-running a job over unchanged data — the first
 * moves only when customers write something new, the second only when the
 * model recomputes. A reader can tell which kind of stale a page is.
 */

export interface DatelineStats {
	/** Newest review timestamp in the corpus behind this page, ISO. */
	reviewsThrough?: string | null
	/** When the score on this page was last computed, ISO. */
	scoredAt?: string | null
	/** Businesses ranked or described. */
	scored?: number
	/** Of those, how many have a synthesized review corpus. */
	analyzed?: number
	/** Reviews read in full across them. */
	reviewsRead?: number
}

export function Dateline({
	stats,
	showByline = true,
}: {
	stats: DatelineStats
	/**
	 * Listings carry the dates but not the byline: the author owns the rubric
	 * and the standards, not a claim about an individual business.
	 */
	showByline?: boolean
}) {
	const reviewsThrough = formatPublicDate(stats.reviewsThrough)
	const scoredAt = formatPublicDate(stats.scoredAt)

	const facts = [
		reviewsThrough ? `Reviews read through ${reviewsThrough}` : null,
		scoredAt ? `Scored ${scoredAt}` : null,
		stats.scored != null ? `${formatPublicNumber(stats.scored)} scored` : null,
		stats.analyzed != null
			? `${formatPublicNumber(stats.analyzed)} analyzed`
			: null,
		stats.reviewsRead
			? `${formatPublicNumber(stats.reviewsRead)} reviews read`
			: null,
	].filter(Boolean) as string[]

	if (facts.length === 0 && !showByline) return null

	return (
		<div className="text-muted-foreground mb-8 space-y-1 font-mono text-xs tabular-nums">
			{facts.length > 0 ? <p>{facts.join(' · ')}</p> : null}
			{showByline ? (
				<p>
					{AUTHOR_BYLINE}.{' '}
					<Link className="underline underline-offset-4" to="/scoring-method">
						How we score
					</Link>
				</p>
			) : null}
		</div>
	)
}
